import PageHeader from "@/components/common/PageHeader";
import SeoHead from "@/components/common/SeoHead";
import DonationForm from "@/components/donation/DonationForm";
import { Heart, ShieldCheck, Sparkles, TrendingUp, Copy, CheckCircle2 } from "lucide-react";
import { BANK_ACCOUNTS, ABOUT } from "@/data/content";
import { useState } from "react";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";

const IMPACT_ILLUSTRATIONS = [
  { amount: "Rp 100.000", body: "Menyediakan makanan bergizi untuk 5 anak selama seminggu" },
  { amount: "Rp 500.000", body: "Dukungan pelatihan keterampilan untuk 1 peserta pemuda" },
  { amount: "Rp 1.000.000", body: "Beasiswa pendidikan untuk 1 anak selama sebulan" },
  { amount: "Rp 5.000.000+", body: "Mendanai 1 program komunitas jangka pendek" },
];

export default function Donation() {
  const [copied, setCopied] = useState(null);
  const { settings } = useSettings();
  const banks = (settings?.bank_accounts && settings.bank_accounts.length > 0)
    ? settings.bank_accounts.map((b) => ({
        bank: b.bank,
        accountName: b.account_name || b.accountName,
        accountNumber: b.account_number || b.accountNumber,
      }))
    : BANK_ACCOUNTS;

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    toast.success("Nomor rekening tersalin");
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div data-testid="page-donation">
      <SeoHead
        title={{ id: "Donasi", en: "Donate" }}
        description={{ id: "Dukung program kemanusiaan, pendidikan, kesehatan, dan lingkungan Sauyunan Foundation melalui donasi transparan dan akuntabel.", en: "Support Sauyunan Foundation's humanitarian, education, health, and environmental programs through transparent and accountable donations." }}
      />
      <PageHeader
        eyebrow="Donasi"
        title="Setiap rupiah Anda adalah harapan bagi banyak orang"
        description="Donasi Anda diinvestasikan langsung ke program kemanusiaan, pendidikan, kesehatan, dan lingkungan yang terukur."
        image="https://images.unsplash.com/photo-1526795443948-005b48ce4791?w=1200&q=80"
      />

      {/* Why donate */}
      <section className="section-y bg-white">
        <div className="container-sf">
          <div className="max-w-3xl mb-12">
            <div className="eyebrow mb-3">Kenapa Berdonasi?</div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight">
              Transparan, akuntabel, dan{" "}
              <span className="font-serif-display italic font-normal text-[var(--brand-orange)]">berdampak</span>.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: ShieldCheck, title: "Tata Kelola Profesional", body: "Sauyunan menerapkan transparansi, akuntabilitas, dan integritas dalam setiap penggunaan sumber daya." },
              { icon: Sparkles, title: "Dampak Terukur", body: "Setiap program dievaluasi berdasarkan model dampak lima tahap: Relief, Recovery, Empowerment, Sustainability, Transformation." },
              { icon: TrendingUp, title: "Ekosistem Terintegrasi", body: "Donasi Anda mendukung ekosistem dampak lintas bidang — bukan satu program terisolasi." },
            ].map((w, i) => (
              <div key={i} className="card-sf p-7">
                <div className={`h-12 w-12 rounded-xl ${i % 2 === 0 ? "bg-[var(--brand-blue)]" : "bg-[var(--brand-orange)]"} text-white flex items-center justify-center mb-5`}>
                  <w.icon className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">{w.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact illustration */}
      <section className="section-y bg-[var(--bg)]">
        <div className="container-sf">
          <div className="max-w-3xl mb-10">
            <div className="eyebrow mb-3">Dampak Donasi Anda</div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight">Ini yang bisa Anda hadirkan</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {IMPACT_ILLUSTRATIONS.map((v, i) => (
              <div key={i} className="card-sf p-6" data-testid={`donation-impact-${i}`}>
                <Heart className="h-6 w-6 text-[var(--brand-orange)] mb-4" />
                <div className="text-xl font-bold text-slate-900">{v.amount}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Online Payment (Midtrans) */}
      <section className="section-y bg-white" id="donasi-online">
        <div className="container-sf">
          <div className="grid lg:grid-cols-[1fr,1.2fr] gap-10 items-start">
            <div className="lg:sticky lg:top-28">
              <div className="eyebrow mb-3">Donasi Online</div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight">
                Berdonasi{" "}
                <span className="font-serif-display italic font-normal text-[var(--brand-orange)]">instan</span>{" "}
                & aman
              </h2>
              <p className="mt-5 text-slate-600 leading-relaxed">
                Dukung program Sauyunan melalui payment gateway resmi <strong>Midtrans</strong> —
                bank transfer, kartu kredit, GoPay, OVO, DANA, ShopeePay, QRIS, dan metode lainnya.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> Terenkripsi & aman (PCI DSS)</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> Konfirmasi otomatis via email</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> Bisa donasi Anonim</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> Pilih program yang Anda dukung</li>
              </ul>
            </div>
            <DonationForm />
          </div>
        </div>
      </section>

      {/* Bank Accounts */}
      <section className="section-y bg-white">
        <div className="container-sf">
          <div className="grid lg:grid-cols-[1fr,1.4fr] gap-10">
            <div className="lg:sticky lg:top-28 self-start">
              <div className="eyebrow mb-3">Rekening Donasi</div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight">Atau transfer melalui rekening resmi</h2>
              <p className="mt-5 text-slate-600 leading-relaxed">Semua rekening di bawah atas nama <strong>Yayasan Sauyunan Rahardja Nusantara</strong>. Setelah transfer, kirim bukti ke <a href="mailto:sauyunan.foundation@gmail.com" className="text-[var(--brand-blue)] font-semibold">sauyunan.foundation@gmail.com</a> untuk mendapatkan kwitansi resmi.</p>
            </div>
            <div className="space-y-4">
              {banks.map((b, i) => (
                <div key={i} className="card-sf p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid={`bank-${b.bank.toLowerCase()}`}>
                  <div>
                    <div className="text-2xl font-bold text-[var(--brand-blue)]">{b.bank}</div>
                    <div className="mt-1 text-sm text-slate-600">a.n. {b.accountName}</div>
                    <div className="mt-3 text-xl font-mono font-bold tracking-wider text-slate-900">{b.accountNumber}</div>
                  </div>
                  <button
                    onClick={() => copy(b.accountNumber.replace(/-/g, ""), b.bank)}
                    className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition ${copied === b.bank ? "bg-emerald-500 text-white" : "bg-slate-900 text-white hover:bg-[var(--brand-orange)]"}`}
                  >
                    {copied === b.bank ? <><CheckCircle2 className="h-4 w-4" /> Tersalin</> : <><Copy className="h-4 w-4" /> Salin Nomor</>}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values reassurance */}
      <section className="section-y bg-gradient-to-br from-[var(--brand-blue)] to-[#254DAA] text-white relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="container-sf relative">
          <div className="text-center max-w-3xl mx-auto">
            <div className="eyebrow text-[var(--brand-orange)] mb-3">Komitmen Kami</div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Setiap donasi dikelola dengan tata kelola profesional dan transparansi penuh.
            </h2>
            <div className="flex flex-wrap justify-center gap-2 mt-8">
              {ABOUT.values.slice(0, 5).map((v) => (
                <div key={v.key} className="rounded-full bg-white/10 border border-white/20 px-5 py-2 text-sm font-semibold">{v.title}</div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
